import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

const CONCERNS = [
  {
    worry: 'I need new content all the time.',
    answer:
      'You make posts, images and videos during the session, and leave with templates to keep going.',
  },
  {
    worry: 'I’m not sure how AI fits my business.',
    answer:
      'Bring your own business case. We show how AI applies to your work, not to a made-up example.',
  },
  {
    worry: 'I don’t want to add another cost.',
    answer:
      'You work on the Unlimited plan during the workshop, so you can see which tools are worth it before you subscribe.',
  },
  {
    worry: 'I don’t have time to learn.',
    answer:
      'Three hours, and you learn by doing. Most of the session is spent on your own laptop.',
  },
  {
    worry: 'I’m not technical.',
    answer:
      'No AI experience needed. We start with the basics of generative AI and build from there.',
  },
  {
    worry: 'What happens to my data?',
    answer:
      'Ask us anything. We don’t use your inputs to train our own models, and our security program follows SOC 2 and ISO 27001.',
  },
];

export default function WorkshopsConcerns() {
  const { ref, state } = useReveal<HTMLUListElement>(0.15);

  return (
    <section className='py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='Sounds familiar?'
          tail='We hear it before every workshop.'
          lead='Most people arrive with the same worries. The session is built to clear every one of them.'
        />

        <ul
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
          )}
        >
          {CONCERNS.map(({ worry, answer }, index) => (
            <li
              key={worry}
              className={clsxm(
                styles.rv,
                'flex flex-col rounded-[22px] p-6 sm:p-7',
                tone.surface,
                tone.line,
              )}
              style={delay(index * 70)}
            >
              <p
                className={clsxm(
                  'text-[19px] font-semibold leading-7 tracking-[-0.015em]',
                  tone.ink,
                )}
              >
                <span aria-hidden='true' className='text-primary-500'>
                  “
                </span>
                {worry}
                <span aria-hidden='true' className='text-primary-500'>
                  ”
                </span>
              </p>
              <p
                className={clsxm(
                  'mt-4 flex gap-2.5 border-t border-[#0f0f28]/[0.07] pt-4 text-[15px] leading-6 dark:border-white/[0.08]',
                  tone.body,
                )}
              >
                <CheckGlyph className='mt-0.5 h-[18px] w-[18px] flex-none' />
                <span>{answer}</span>
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
